import type { TopSideNavBarItem } from "@octopusdeploy/design-system-components";
import { DeployIcon, InsightsIcon, TasksIcon, CheckIcon } from "@octopusdeploy/design-system-icons";
import { Permission } from "@octopusdeploy/octopus-server-client";
import { links } from "@octopusdeploy/portal-routes";
import { session } from "~/clientInstance";
import { isFeatureToggleEnabled } from "~/components/FeatureToggle/New/FeatureToggleContext";
import type { SpaceContext } from "~/components/SpaceLoader/SpaceLoader";
import { isSpaceNotFound, isSpecificSpaceContext } from "~/components/SpaceLoader/SpaceLoader";
import { usePageArea } from "~/routing/PageAreaContext";
import { hasPermissionsInAnyScope } from "./hasPermissionInAnyScope";
export function useTopSideBarNavItems(spaceContext: SpaceContext): TopSideNavBarItem[] {
    const spaceSpecificNavItems = useSpaceSpecificNavigationAreas(spaceContext);
    return [
        ...spaceSpecificNavItems,
        {
            type: "link",
            label: "Tasks",
            icon: <TasksIcon size={24}/>,
            href: links.tasksPage.generateUrl(),
            showLinkAsActive: "if path partially matches",
            isVisible: hasTaskViewPermission(spaceContext),
        },
        {
            type: "link",
            label: "Approvals",
            icon: <CheckIcon size={24}/>,
            href: links.approvalsPage.generateUrl(),
            showLinkAsActive: "if path partially matches",
            isVisible: hasApprovalsAvailable(),
        },
    ];
}
function useSpaceSpecificNavigationAreas(spaceContext: SpaceContext): TopSideNavBarItem[] {
    const pageArea = usePageArea();
    if (!isSpecificSpaceContext(spaceContext))
        return [];
    const spaceId = spaceContext.Id;
    return [
        {
            type: "link",
            label: "Deploy",
            icon: <DeployIcon size={24}/>,
            href: links.projectsPage.generateUrl({ spaceId }),
            showLinkAsActive: pageArea?.mainArea === "Deploy" ? "always" : "never",
        },
        {
            type: "link",
            label: "Insights",
            icon: <InsightsIcon size={24}/>,
            href: links.insightsRootRedirect.generateUrl({ spaceId }),
            showLinkAsActive: "if path partially matches",
            isVisible: hasPermissionsInAnyScope(spaceId, Permission.InsightsReportView),
        },
    ];
}
function hasTaskViewPermission(spaceContext: SpaceContext): boolean {
    if (!isSpaceNotFound(spaceContext) || spaceContext.isAlsoInSystemContext) {
        return session.currentPermissions?.hasPermissionInAnyScope(Permission.TaskView) ?? false;
    }
    return false;
}
function hasApprovalsAvailable(): boolean {
    return isFeatureToggleEnabled("octopus-approvals");
}
